import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  MeterDto,
  MeteringPointCalculationParameterDto,
  MeteringPointDto,
  MeteringPointService,
  MeterService,
  Quadrant,
  RequestMasterDataDto
} from '@app/api/dmc';
import { FormApiService } from '@app/core/services/form-api.service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { CalculationFormComponent } from '@views/calculation/calculation-form/calculation-form.component';
import { switchMap, tap } from 'rxjs/operators';
import { filter, of, Subscription } from 'rxjs';
import { removeNullValues } from '@app/core/utils/remove-null-values';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';
import { dateGreaterThanEqualValidator } from '@shared/validators/date-greater-than-equal.validator';
import { dateLessThanEqualValidator } from '@shared/validators/date-less-than-equal.validator';
import { AlertComponent } from '@shared/components/alert/alert.component';

/** How the connected metering point's time series is produced. */
export type ConnectionSource = 'meter' | 'calculation';

/** Number of calendar days back in time a metering point may be connected. */
export const CONNECT_MAX_DAYS_BACK = 7;

/**
 * Connects a newly created metering point (BRS-008). The user explicitly chooses whether the time series
 * comes from a meter quadrant or a calculation formula, independent of the metering point's subtype.
 */
@Component({
  selector: 'app-connect-modal',
  imports: [
    ReactiveFormsModule,
    InputGroupComponent,
    LabelDirective,
    InputDirective,
    SelectDirective,
    TranslatePipe,
    InputDateDirective,
    CalculationFormComponent,
    AlertComponent,
  ],
  templateUrl: './connect-modal.component.html',
  styleUrl: './connect-modal.component.css'
})
export class ConnectModalComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  public meteringPoint?: MeteringPointDto;

  public element?: any;

  public readonly minValidFrom = ConnectModalComponent.toLocalDateString(-CONNECT_MAX_DAYS_BACK);
  public readonly maxValidFrom = ConnectModalComponent.toLocalDateString(0);

  public form: FormGroup = new FormGroup({
    meteringPointIdentification: new FormControl('', Validators.required),
    validFrom: new FormControl('', [
      Validators.required,
      dateGreaterThanEqualValidator(-CONNECT_MAX_DAYS_BACK),
      dateLessThanEqualValidator(0),
    ]),
    createIfNotExists: new FormControl(false),
    bypassBalanceSupplierValidation: new FormControl(false),
    connectionSource: new FormControl<ConnectionSource | null>(null, Validators.required),

    meteringPointCalculationStructure: new FormGroup({
      calculationStructureInternalName: new FormControl(null, Validators.required),
      calculationStructureIsDynamic: new FormControl(),
      parameters: new FormControl(null),
      validFrom: new FormControl(null),
      meteringPointIdentification: new FormControl(null),
    }),

    meterIdentification: new FormControl<string | null>(null, Validators.required),
    meterSerialNumber: new FormControl(''),
    quadrant: new FormControl(null, Validators.required),
    meterNumber: new FormControl(''),
    startMeterStand: new FormControl(''),
  });

  public meterList: MeterDto[] = [];
  public quadrant: KeyValue[] = enumToKeyValue(Quadrant, {omitKeys: ['NotSpecified'], prefixValue: 'enum.quadrant.'});

  public rowHandler: MeteringPointCalculationParameterDto[] = [];
  public algorithmDynamic = false;

  private readonly meterControlNames = ['meterIdentification', 'meterSerialNumber', 'quadrant', 'meterNumber', 'startMeterStand'];
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly meteringPointService: MeteringPointService,
    private readonly formApiService: FormApiService,
    private readonly meterService: MeterService
  ) {
  }

  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public get calculationForm(): FormGroup {
    return this.form.get('meteringPointCalculationStructure') as FormGroup;
  }

  public get connectionSource(): ConnectionSource | null {
    return this.form.get('connectionSource')?.value ?? null;
  }

  public get connectionSourceInvalid(): boolean {
    const control = this.form.get('connectionSource');
    return !!control && control.invalid && control.touched;
  }

  public get currentSubtypeKey(): string | null {
    const subType = this.meteringPoint?.detailCharacteristics?.subType;
    return subType ? `enum.meteringpoint.meteringpointsubtype.${subType.toLowerCase()}` : null;
  }

  public get selectedMeter(): MeterDto | undefined {
    const meterId = this.form.get('meterIdentification')?.value;
    return this.meterList.find(m => m.id === meterId);
  }

  public getRegisterForSelectedMeter(quadrant: string | null): string | null {
    if (!quadrant || !this.selectedMeter) {
      return null;
    }

    return this.getRegisterForQuadrant(this.selectedMeter, quadrant as Quadrant);
  }

  ngOnInit(): void {
    this.applyConnectionSource(null);
    this.loadMeteringPointAndMeters();

    this.subscriptions.add(this.form.get('meterIdentification')!.valueChanges.subscribe(meterId => {
      const found = this.meterList.find(m => m.id === meterId);
      this.form.get('meterSerialNumber')?.setValue(found?.serialNumber ?? '');
      this.form.get('meterNumber')?.setValue(found?.meterNumber ?? '');
    }));

    this.subscriptions.add(this.form.get('connectionSource')!.valueChanges.subscribe(source => this.applyConnectionSource(source)));

    if (this.element?.meteringPointIdentification) {
      this.form.get('meteringPointIdentification')?.setValue(this.element?.meteringPointIdentification);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public selectConnectionSource(source: ConnectionSource): void {
    const control = this.form.get('connectionSource')!;
    control.setValue(source);
    control.markAsTouched();
    control.markAsDirty();
  }

  /** Loads the metering point (with subtype) and its meters; meters are always loaded since the source is user-selected. **/
  private loadMeteringPointAndMeters(): void {
    const identification = this.meteringPoint?.identification ?? this.element?.meteringPointIdentification;
    const meteringPoint$ = this.meteringPoint?.detailCharacteristics || !identification
      ? of(this.meteringPoint)
      : this.meteringPointService.meteringPointGetByIdentificationGet(identification);

    this.subscriptions.add(meteringPoint$
      .pipe(
        tap(mp => {
          this.meteringPoint = mp ?? this.meteringPoint;
          this.form.get('meteringPointIdentification')?.setValue(this.meteringPoint?.identification ?? this.element?.meteringPointIdentification ?? '');
        }),
        filter(() => !!this.meteringPoint?.identification),
        switchMap(() => this.meterService.meterGetbymeteringpointGet(this.meteringPoint!.identification!))
      )
      .subscribe(meters => (this.meterList = meters)));
  }

  /** Enables only the controls belonging to the chosen source, so the unused source is neither validated nor submitted. **/
  private applyConnectionSource(source: ConnectionSource | null): void {
    const useMeter = source === 'meter';
    const useCalculation = source === 'calculation';

    for (const name of this.meterControlNames) {
      const control = this.form.get(name)!;
      if (useMeter) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    }

    if (useCalculation) {
      this.calculationForm.enable({ emitEvent: false });
    } else {
      this.calculationForm.disable({ emitEvent: false });
    }
  }

  public submit(): void {
    this.form.get('meteringPointIdentification')?.setValue(this.meteringPoint?.identification);

    if (this.connectionSource === 'calculation') {
      const calcForm = this.calculationForm;
      calcForm.get('calculationStructureIsDynamic')?.setValue(this.algorithmDynamic);
      calcForm.get('parameters')?.setValue(this.rowHandler);
      calcForm.get('validFrom')?.setValue(this.form.get('validFrom')?.value);
      calcForm.get('meteringPointIdentification')?.setValue(this.meteringPoint?.identification);
    }

    this.formApiService.submit<RequestMasterDataDto>(
      this.form,
      data => this.meteringPointService.meteringPointConnectMeteringPointPut(data),
      {
        preprocess: (data: any) => {
          const cleaned: any = { ...removeNullValues(data) };
          delete cleaned.connectionSource;

          if (cleaned.meteringPointCalculationStructure) {
            cleaned.meteringPointCalculationStructure = removeNullValues(cleaned.meteringPointCalculationStructure);
          }

          return cleaned;
        },
        successMessage: 'meteringPoint.action.connect.success',
        closeModal: true,
      }
    );
  }

  private getRegisterForQuadrant(meter: MeterDto, quadrant: Quadrant): string | null {
    if (meter.mappingA14 === quadrant) {
      return 'A14';
    }

    if (meter.mappingA23 === quadrant) {
      return 'A23';
    }

    if (meter.mappingR12 === quadrant) {
      return 'R12';
    }

    if (meter.mappingR34 === quadrant) {
      return 'R34';
    }

    return null;
  }

  private static toLocalDateString(offsetDays: number): string {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  }
}
